import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigation } from "@refinedev/core";
import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Send,
} from "@/components/ui/icon";

import type { Skill } from "@aos/shared";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { StatusBadge } from "@/components/app/StatusBadge";
import { StatusAction } from "./_components/StatusAction";

const UNGROUPED = "(none)";

/** Page size used when scanning the full skill catalog (mirrors Package Graph). */
const SKILL_PAGE_SIZE = 200;

interface ListEnvelope<T> {
  readonly items: readonly T[];
  readonly total: number;
}

/**
 * Load EVERY skill, page by page. The Skill Tree groups the whole catalog, so a
 * single capped page would silently drop skills once the catalog grows past the
 * page size (e.g. a freshly captured skill sorting mid-alphabet past row 200).
 * This mirrors the Package Graph's `fetchAllPages` so no skill is hidden.
 */
function useAllSkills() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const refetch = useCallback(() => setReloadKey((value) => value + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);

    (async () => {
      const items: Skill[] = [];
      let page = 1;
      while (true) {
        const envelope = await api.get<ListEnvelope<Skill>>("/skills", {
          query: { page, pageSize: SKILL_PAGE_SIZE, sort: "name", order: "asc" },
          signal: controller.signal,
        });
        items.push(...envelope.items);
        if (items.length >= envelope.total || envelope.items.length === 0) break;
        page += 1;
      }
      return items;
    })()
      .then((items) => {
        setSkills(items);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err : new Error("Failed to load skills."));
        setIsLoading(false);
      });

    return () => controller.abort();
  }, [reloadKey]);

  return { skills, isLoading, isError: error !== null, error, refetch };
}

interface DepartmentGroup {
  department: string;
  skills: Skill[];
}
interface DomainGroup {
  domain: string;
  departments: DepartmentGroup[];
  count: number;
}

/** Group a flat skill list by domain → department. */
function buildTree(skills: Skill[]): DomainGroup[] {
  const byDomain = new Map<string, Map<string, Skill[]>>();
  for (const skill of skills) {
    const domain = skill.domain ?? UNGROUPED;
    const department = skill.department ?? UNGROUPED;
    let depts = byDomain.get(domain);
    if (!depts) {
      depts = new Map<string, Skill[]>();
      byDomain.set(domain, depts);
    }
    const list = depts.get(department) ?? [];
    list.push(skill);
    depts.set(department, list);
  }

  const domains: DomainGroup[] = [];
  for (const [domain, depts] of byDomain) {
    const departments: DepartmentGroup[] = [];
    let count = 0;
    for (const [department, list] of depts) {
      const sorted = [...list].sort((a, b) => a.name.localeCompare(b.name));
      departments.push({ department, skills: sorted });
      count += sorted.length;
    }
    departments.sort((a, b) => a.department.localeCompare(b.department));
    domains.push({ domain, departments, count });
  }
  domains.sort((a, b) => a.domain.localeCompare(b.domain));
  return domains;
}

/**
 * Hierarchical skill browser grouped domain → department → skill. Each leaf
 * shows a status badge and the next approval action (draft → review →
 * published) inline, plus a link to the full skill detail page.
 */
export function SkillTree() {
  const { show, list } = useNavigation();
  const { skills, isLoading, isError, error, refetch } = useAllSkills();

  const tree = useMemo(() => buildTree(skills), [skills]);

  const refresh = () => {
    void refetch();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Skill Tree"
        subtitle="Skills grouped by domain and department, with inline approvals."
        icon="packages"
        actions={
          <Button variant="outline" onClick={() => list("skills")}>
            List view
          </Button>
        }
      />

      {!isLoading && !isError ? (
        <SkillReviewQueue skills={skills} onDone={refresh} show={show} />
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Catalog</CardTitle>
          <CardDescription>
            Grouped by domain and department. {skills.length} skill
            {skills.length === 1 ? "" : "s"} total.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState rows={6} />
          ) : isError ? (
            <ErrorState
              message={error?.message ?? "Failed to load skills."}
              onRetry={refresh}
            />
          ) : tree.length === 0 ? (
            <EmptyState
              icon="packages"
              title="No skills yet"
              hint="Create a skill to see it grouped in the tree."
            />
          ) : (
            <div className="space-y-1">
              {tree.map((domain) => (
                <DomainNode
                  key={domain.domain}
                  domain={domain}
                  onShow={(id) => show("skills", id)}
                  onDone={refresh}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DomainNode({
  domain,
  onShow,
  onDone,
}: {
  domain: DomainGroup;
  onShow: (id: string) => void;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-semibold hover:bg-accent"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
        <span>{domain.domain}</span>
        <span className="text-xs font-normal text-muted-foreground">
          ({domain.count})
        </span>
      </button>
      {open ? (
        <div className="ml-4 border-l border-border pl-3">
          {domain.departments.map((dept) => (
            <DepartmentNode
              key={dept.department}
              group={dept}
              onShow={onShow}
              onDone={onDone}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function DepartmentNode({
  group,
  onShow,
  onDone,
}: {
  group: DepartmentGroup;
  onShow: (id: string) => void;
  onDone: () => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        type="button"
        className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium hover:bg-accent"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5" />
        )}
        <span>{group.department}</span>
        <span className="text-xs font-normal text-muted-foreground">
          ({group.skills.length})
        </span>
      </button>
      {open ? (
        <ul className="ml-4 border-l border-border pl-3">
          {group.skills.map((skill) => (
            <li
              key={skill.id}
              className="flex items-center justify-between gap-2 rounded-md px-2 py-1.5 hover:bg-accent"
            >
              <button
                type="button"
                className="truncate text-left text-sm hover:underline"
                onClick={() => onShow(skill.id)}
              >
                {skill.name}
              </button>
              <div className="flex shrink-0 items-center gap-2">
                <StatusBadge status={skill.status} />
                <LeafApproval skill={skill} onDone={onDone} />
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** Inline next-step approval button for a skill leaf in the tree. */
function LeafApproval({ skill, onDone }: { skill: Skill; onDone: () => void }) {
  if (skill.status === "draft") {
    return (
      <StatusAction
        resource="skills"
        id={skill.id}
        toStatus="review"
        label="Review"
        icon={Send}
        variant="outline"
        onDone={onDone}
      />
    );
  }
  if (skill.status === "review") {
    return (
      <StatusAction
        resource="skills"
        id={skill.id}
        toStatus="published"
        label="Publish"
        icon={CheckCircle2}
        onDone={onDone}
      />
    );
  }
  return null;
}

/**
 * A queue of skills awaiting review (status === 'review'), surfaced at the top
 * of the tree so approvers can clear them quickly. Renders nothing when empty.
 */
export function SkillReviewQueue({
  skills,
  onDone,
  show,
}: {
  skills: Skill[];
  onDone: () => void;
  show: (resource: string, id: string) => void;
}) {
  const pending = useMemo(
    () => skills.filter((s) => s.status === "review"),
    [skills],
  );
  if (pending.length === 0) {
    return null;
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Review Queue</CardTitle>
        <CardDescription>
          {pending.length} skill{pending.length === 1 ? "" : "s"} awaiting
          approval.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {pending.map((skill) => (
            <li
              key={skill.id}
              className="flex items-center justify-between gap-2 py-2"
            >
              <button
                type="button"
                className="text-left text-sm font-medium hover:underline"
                onClick={() => show("skills", skill.id)}
              >
                {skill.name}
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {skill.domain ?? UNGROUPED} / {skill.department ?? UNGROUPED}
                </span>
              </button>
              <div className="flex shrink-0 items-center gap-2">
                <StatusAction
                  resource="skills"
                  id={skill.id}
                  toStatus="draft"
                  label="Reject"
                  variant="outline"
                  onDone={onDone}
                />
                <StatusAction
                  resource="skills"
                  id={skill.id}
                  toStatus="published"
                  label="Approve"
                  icon={CheckCircle2}
                  onDone={onDone}
                />
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

export default SkillTree;
